
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { KnowledgeCollection, KnowledgeArticle } from "@/types";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Settings, Book } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import KnowledgeCollectionsList from "./knowledge/KnowledgeCollectionsList";
import { KnowledgeArticleEditor } from "./knowledge/KnowledgeArticleEditor";
import KnowledgeCollectionForm from "./knowledge/KnowledgeCollectionForm";

type KnowledgeBaseManagementProps = {
  companyId: string;
};

export const KnowledgeBaseManagement = ({
  companyId,
}: KnowledgeBaseManagementProps) => {
  const [collections, setCollections] = useState<KnowledgeCollection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCollectionId, setSelectedCollectionId] = useState<
    string | null
  >(null);
  const [selectedArticleId, setSelectedArticleId] = useState<string | null>(
    null
  );
  const [isCreatingCollection, setIsCreatingCollection] = useState(false);
  const [isCreatingArticle, setIsCreatingArticle] = useState(false);
  const [collectionArticles, setCollectionArticles] = useState<Record<string, KnowledgeArticle[]>>({});
  const { toast } = useToast();

  const fetchCollections = async () => {
    if (!companyId) return;

    try {
      setIsLoading(true);
      const { data, error } = await (
        supabase.from("knowledge_collections") as any
      )
        .select("*")
        .eq("company_id", companyId)
        .order("position", { ascending: true });

      if (error) {
        console.error("Error fetching knowledge collections:", error);
        toast({
          title: "Error",
          description: "Failed to load knowledge base collections",
          variant: "destructive",
        });
        return;
      }

      setCollections(data || []);

      // Select the first collection if none is selected
      if (data && data.length > 0 && !selectedCollectionId) {
        setSelectedCollectionId(data[0].id);
      }
    } catch (error) {
      console.error("Unexpected error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCollections();
  }, [companyId]);

  const handleCollectionCreated = () => {
    fetchCollections();
    setIsCreatingCollection(false);
  };

  const handleCollectionDeleted = () => {
    fetchCollections();
    setSelectedCollectionId(null);
    setSelectedArticleId(null);
  };

  const handleArticleCreated = (articleData?: any) => {
    // Update the collectionArticles state if we have article data
    if (articleData && selectedCollectionId) {
      if (articleData.deleted) {
        // Handle article deletion
        setCollectionArticles(prev => {
          const articles = prev[selectedCollectionId] || [];
          return {
            ...prev,
            [selectedCollectionId]: articles.filter(a => a.id !== articleData.id)
          };
        });
      } else {
        // Handle article creation or update
        setCollectionArticles(prev => {
          const articles = [...(prev[selectedCollectionId] || [])];
          const index = articles.findIndex(a => a.id === articleData.id);
          
          if (index >= 0) {
            // Update existing article
            articles[index] = articleData;
          } else {
            // Add new article
            articles.push(articleData);
          }
          
          return {
            ...prev,
            [selectedCollectionId]: articles
          };
        });
      }
    }

    // Reset UI state
    setIsCreatingArticle(false);
    setSelectedArticleId(null);
  };
  
  // Handler for article list updates from KnowledgeCollectionsList
  const handleArticleListChange = (collectionId: string, articles: KnowledgeArticle[]) => {
    setCollectionArticles(prev => ({
      ...prev,
      [collectionId]: articles
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold">Knowledge Base</h2>
        <Button onClick={() => setIsCreatingCollection(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New Collection
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center p-8">
          <p className="text-muted-foreground">Loading knowledge base...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="md:col-span-1">
            <KnowledgeCollectionsList
              collections={collections}
              selectedCollectionId={selectedCollectionId}
              onSelectCollection={setSelectedCollectionId}
              onDeleteCollection={handleCollectionDeleted}
              onCreateArticle={() => {
                setIsCreatingArticle(true);
                setSelectedArticleId(null);
              }}
              onSelectArticle={setSelectedArticleId}
              onArticleChange={handleArticleListChange}
            />
          </div>
          <div className="md:col-span-3">
            {selectedCollectionId && (
              <>
                {isCreatingArticle || selectedArticleId ? (
                  <KnowledgeArticleEditor
                    companyId={companyId}
                    collectionId={selectedCollectionId}
                    articleId={selectedArticleId}
                    isCreating={isCreatingArticle}
                    onCancel={() => {
                      setIsCreatingArticle(false);
                      setSelectedArticleId(null);
                    }}
                    onSave={handleArticleCreated}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center h-64 bg-muted rounded-lg p-8 text-center">
                    <Book className="h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-xl font-medium mb-2">
                      No Article Selected
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      Select an article to edit or create a new one.
                    </p>
                    <Button onClick={() => setIsCreatingArticle(true)}>
                      <Plus className="mr-2 h-4 w-4" />
                      Create Article
                    </Button>
                  </div>
                )}
              </>
            )}
            {!selectedCollectionId && !isCreatingCollection && (
              <div className="flex flex-col items-center justify-center h-64 bg-muted rounded-lg p-8 text-center">
                <Book className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-xl font-medium mb-2">
                  No Collection Selected
                </h3>
                <p className="text-muted-foreground mb-4">
                  Select a collection to manage its articles or create a new
                  collection.
                </p>
                <Button onClick={() => setIsCreatingCollection(true)}>
                  <Plus className="mr-2 h-4 w-4" />
                  Create Collection
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Collection Creation Modal */}
      {isCreatingCollection && (
        <KnowledgeCollectionForm
          companyId={companyId}
          onCancel={() => setIsCreatingCollection(false)}
          onSave={handleCollectionCreated}
        />
      )}
    </div>
  );
};

export default KnowledgeBaseManagement;
